/**
 * The typed piece form: a piece type picker over the piece-type registry
 * (`lib/editor/pieceTypes.ts` — a prop, so the registry can later come from
 * studio config, D22), the chosen type's parameters as fields (each a leaf of
 * `params`, committed alone so concurrent edits of two parameters both win),
 * and the piece's timing (`enable`: start — a time or "now" — and duration).
 *
 * A piece of a type the registry does not know keeps its type and shows its
 * raw parameters read-only, rather than losing them to a form that cannot
 * hold them.
 */
import type { JsonValue } from '@acp/domain/primitives.js';
import { type KeyboardEvent, useRef, useState } from 'react';
import { formatDuration, parseDuration } from '../../lib/editor/duration.js';
import type { PieceParamSpec, PieceTypeSpec } from '../../lib/editor/pieceTypes.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';

export type PieceTiming = { readonly start: number | 'now'; readonly duration: number | null };

export type PieceFormProps = {
  readonly types: readonly PieceTypeSpec[];
  readonly pieceType: string;
  readonly params: Readonly<Record<string, JsonValue>>;
  readonly timing: PieceTiming;
  /** A new type was picked (the view also sets its default source layer). */
  readonly onTypeChange: (spec: PieceTypeSpec) => void;
  readonly onParamCommit: (key: string, value: JsonValue) => void;
  readonly onTimingCommit: (timing: PieceTiming) => void;
  readonly onFocusField: (path: string | null) => void;
};

const SELECT_CLASS = 'h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50';

function display(value: JsonValue | undefined, kind: PieceParamSpec['kind']): string {
  if (value === undefined || value === null) return '';
  if (kind === 'json' || typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parseParam(raw: string, kind: PieceParamSpec['kind']): { ok: true; value: JsonValue } | { ok: false; error: string } {
  if (kind === 'text') return { ok: true, value: raw };
  if (kind === 'number') {
    const n = Number(raw);
    return raw.trim() !== '' && Number.isFinite(n) ? { ok: true, value: n } : { ok: false, error: 'not a number' };
  }
  if (raw.trim() === '') return { ok: true, value: null };
  try {
    const parsed: unknown = JSON.parse(raw);
    return { ok: true, value: toJson(parsed) };
  } catch {
    return { ok: false, error: 'not JSON' };
  }
}

/** JSON.parse output is JSON by construction; this narrows `unknown` without an assertion. */
function toJson(value: unknown): JsonValue {
  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map(toJson);
  if (typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, toJson(v)]));
  return null;
}

function DraftInput(props: { readonly id: string; readonly initial: string; readonly placeholder: string; readonly mono: boolean; readonly validate: (raw: string) => string | null; readonly onCommit: (raw: string) => void; readonly onFocus: () => void; readonly onBlur: () => void }) {
  const { id, initial, placeholder, mono, validate, onCommit, onFocus, onBlur } = props;
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  // What was last committed: Enter commits, and the blur that follows must not commit it again.
  const committed = useRef(initial);
  const commit = () => {
    if (draft === committed.current) return;
    const problem = validate(draft);
    setError(problem);
    if (problem !== null) return;
    committed.current = draft;
    onCommit(draft);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };
  return (
    <>
      <Input
        id={id}
        className={mono ? 'h-8 font-mono' : 'h-8'}
        value={draft}
        placeholder={placeholder}
        aria-invalid={error !== null}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={onFocus}
        onBlur={() => {
          commit();
          onBlur();
        }}
        onKeyDown={onKeyDown}
      />
      {error === null ? null : (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

function startText(start: number | 'now'): string {
  return start === 'now' ? 'now' : formatDuration(start);
}

export function PieceForm({ types, pieceType, params, timing, onTypeChange, onParamCommit, onTimingCommit, onFocusField }: PieceFormProps) {
  const spec = types.find((t) => t.id === pieceType) ?? null;
  const rawParams = Object.entries(params);
  return (
    <section className="grid gap-3 border-t pt-3" data-testid="piece-form" aria-label="Piece">
      <div className="grid gap-1">
        <Label htmlFor="piece-type" className="text-xs text-muted-foreground">
          Piece type
        </Label>
        <select
          id="piece-type"
          className={SELECT_CLASS}
          value={spec === null ? '' : spec.id}
          onFocus={() => onFocusField('pieceType')}
          onBlur={() => onFocusField(null)}
          onChange={(e) => {
            const next = types.find((t) => t.id === e.target.value);
            if (next !== undefined) onTypeChange(next);
          }}
        >
          {spec === null ? <option value="">{pieceType === '' ? 'Choose a type…' : `${pieceType} (unknown)`}</option> : null}
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      {spec?.params.map((p) => (
        <div className="grid gap-1" key={`${spec.id}.${p.key}`}>
          <Label htmlFor={`param-${p.key}`} className="text-xs text-muted-foreground">
            {p.label}
          </Label>
          <DraftInput
            key={`${p.key}:${display(params[p.key], p.kind)}`}
            id={`param-${p.key}`}
            initial={display(params[p.key], p.kind)}
            placeholder={p.placeholder}
            mono={p.kind !== 'text'}
            validate={(raw) => {
              const parsed = parseParam(raw, p.kind);
              return parsed.ok ? null : parsed.error;
            }}
            onCommit={(raw) => {
              const parsed = parseParam(raw, p.kind);
              if (parsed.ok) onParamCommit(p.key, parsed.value);
            }}
            onFocus={() => onFocusField(`params.${p.key}`)}
            onBlur={() => onFocusField(null)}
          />
        </div>
      ))}
      {spec === null && rawParams.length > 0 ? (
        <pre className="overflow-auto rounded-md bg-muted p-2 font-mono text-xs" data-testid="raw-params">
          {JSON.stringify(params, null, 2)}
        </pre>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <div className="grid gap-1">
          <Label htmlFor="piece-start" className="text-xs text-muted-foreground">
            Start
          </Label>
          <DraftInput
            key={`start:${startText(timing.start)}`}
            id="piece-start"
            initial={startText(timing.start)}
            placeholder="0:00 or now"
            mono
            validate={(raw) => (raw.trim() === 'now' || typeof parseDuration(raw) === 'number' ? null : 'a time (m:ss) or “now”')}
            onCommit={(raw) => {
              const ms = parseDuration(raw);
              if (raw.trim() === 'now') onTimingCommit({ ...timing, start: 'now' });
              else if (typeof ms === 'number') onTimingCommit({ ...timing, start: ms });
            }}
            onFocus={() => onFocusField('enable')}
            onBlur={() => onFocusField(null)}
          />
        </div>
        <div className="grid gap-1">
          <Label htmlFor="piece-duration" className="text-xs text-muted-foreground">
            Duration
          </Label>
          <DraftInput
            key={`duration:${String(timing.duration)}`}
            id="piece-duration"
            initial={timing.duration === null ? '' : formatDuration(timing.duration)}
            placeholder="until ended"
            mono
            validate={(raw) => (parseDuration(raw) === undefined ? 'a duration (m:ss), or empty' : null)}
            onCommit={(raw) => {
              const ms = parseDuration(raw);
              if (ms !== undefined) onTimingCommit({ ...timing, duration: ms });
            }}
            onFocus={() => onFocusField('enable')}
            onBlur={() => onFocusField(null)}
          />
        </div>
      </div>
    </section>
  );
}
